import React, { Component } from 'react';
import { Button, Col, Row, Form, Input, CustomInput } from 'reactstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { StyledCard } from '../../Base/Common/CommonUIComponents';
import CustomSelect from '../../Base/Common/CustomSelect';
import { CustomTable } from '../../Base/Common/CustomTable';
import { getAPI } from '../../Base/API';
import { handleNotification } from '../../Base/Common/Notification';

// Admin-only "Entity Records" inspector. Mirrors the GMS (ProfileNow)
// "Registos por Entidade" page, adapted to Voucher conventions:
//  - entity + column dropdowns are driven by the backend /metadata endpoint
//    (single source of truth for the whitelist, column types and masked flags),
//  - results are returned pre-masked and tenant-scoped by the backend.
class EntityRecords extends Component {

    maxFilters = 3;
    // Deliberate instance fields, NOT React state: they never affect rendering (used only as
    // stable React key sources) and must survive re-renders without causing them. Do not move
    // into state; in a hooks refactor these become useRefs. searchGeneration namespaces the
    // result row keys per search so keys never repeat across successive searches.
    nextCriterionId = 0;
    searchGeneration = 0;

    constructor(props) {
        super(props);
        this.state = {
            block: true,
            error: [],
            entityOptions: [],       // [{ value, label }] for the entity selector
            columnsByEntity: {},     // entityName -> [{ value, label, masked, type }]
            entityName: null,
            searchCriteria: [],      // [{ id, colName, colValue }]
            includeDeleted: false,
            records: [],             // rows returned by the last search (+ synthetic _rowKey)
            tableColumns: [],        // CustomTable column defs for the current result
            truncated: false,
            rowCap: 0
        };
    }

    componentDidMount() {
        this.getMetadata();
    }

    getMetadata = () => {
        getAPI(result => {
            const { data, error } = result;
            const errorMessage = [];

            if (error) {
                errorMessage.push({ message: error.message, stack: error.stack, messageType: 'danger' });
                this.setState({ error: errorMessage, block: false });
                return;
            }
            if (data) {
                if (data.errors && data.errors.length > 0) {
                    handleNotification(data);
                    this.setState({ error: errorMessage, block: false });
                }
                else if (data.response) {
                    const entityOptions = data.response.map(e => ({ value: e.entity, label: e.entity }));
                    const columnsByEntity = {};
                    data.response.forEach(e => {
                        columnsByEntity[e.entity] = (e.columns || []).map(c => ({
                            value: c.name,
                            label: c.masked ? `${c.name} (${c.type}) \u{1F512}` : `${c.name} (${c.type})`,
                            masked: c.masked,
                            type: c.type,
                            filterable: c.filterable
                        }));
                    });
                    this.setState({ entityOptions, columnsByEntity, error: errorMessage, block: false });
                }
                else {
                    this.setState({ error: errorMessage, block: false });
                }
            }
            else {
                this.setState({ error: errorMessage, block: false });
            }
        }, `/api/Voucher/v1/EntityRecords/metadata`);
    }

    handleEntity = (combo) => {
        this.setState({
            entityName: combo ? combo.value : null,
            searchCriteria: [],
            records: [],
            tableColumns: [],
            truncated: false
        });
    }

    handleAddCriterion = (combo) => {
        if (!combo) return;
        const { searchCriteria } = this.state;
        if (searchCriteria.length >= this.maxFilters) return;

        this.setState({
            searchCriteria: [...searchCriteria, { id: this.nextCriterionId++, colName: combo.value, colValue: '' }]
        });
    }

    handleValueChange = (evt, idx) => {
        if (evt && evt.target) {
            const { value } = evt.target;
            this.setState(prev => ({
                searchCriteria: prev.searchCriteria.map((crt, i) => i === idx ? { ...crt, colValue: value } : crt)
            }));
        }
    }

    removeCriterion = (idx) => {
        this.setState(prev => ({
            searchCriteria: prev.searchCriteria.filter((_, i) => i !== idx)
        }));
    }

    toggleIncludeDeleted = () => {
        this.setState(prev => ({ includeDeleted: !prev.includeDeleted }));
    }

    getRecords = (e) => {
        e.preventDefault();

        const { entityName, searchCriteria, includeDeleted } = this.state;

        const criteriaQuery = searchCriteria.reduce(
            (acc, curr) => `${acc}&colNames=${encodeURIComponent(curr.colName)}&colValues=${encodeURIComponent(curr.colValue)}`,
            ''
        );

        this.setState({ block: true });
        getAPI(result => {
            const { data, error } = result;
            const errorMessage = [];

            if (error) {
                errorMessage.push({ message: error.message, stack: error.stack, messageType: 'danger' });
                this.setState({ error: errorMessage, block: false });
                return;
            }
            if (data) {
                if (data.errors && data.errors.length > 0) {
                    handleNotification(data);
                    this.setState({ error: errorMessage, block: false });
                }
                else if (data.response && data.response.length > 0) {
                    const entityResult = data.response[0];
                    // Row dictionary keys keep their PascalCase name (the serializer does not
                    // camel-case dictionary keys, unlike regular DTO properties), so dataField
                    // uses the column name as-is.
                    const tableColumns = (entityResult.columns || []).map(c => ({
                        dataField: c.name,
                        text: c.masked ? `${c.name} \u{1F512}` : c.name,
                        sort: true,
                        // Defensive: never hand React a raw object (would crash the grid).
                        formatter: cell => (cell === null || cell === undefined)
                            ? ''
                            : (typeof cell === 'object' ? JSON.stringify(cell) : String(cell))
                    }));
                    const gen = ++this.searchGeneration;
                    const records = (entityResult.rows || []).map((row, idx) => ({ ...row, _rowKey: `${gen}-${idx}` }));

                    this.setState({
                        records,
                        tableColumns,
                        truncated: entityResult.truncated,
                        rowCap: entityResult.rowCap,
                        error: errorMessage,
                        block: false
                    });
                }
                else {
                    this.setState({ records: [], tableColumns: [], truncated: false, error: errorMessage, block: false });
                }
            }
            else {
                this.setState({ error: errorMessage, block: false });
            }
        }, `/api/Voucher/v1/EntityRecords/recordsByEntity?entityName=${encodeURIComponent(entityName)}${criteriaQuery}&includeDeleted=${includeDeleted}`);
    }

    render() {
        const { block, error, entityOptions, columnsByEntity, entityName, searchCriteria, includeDeleted, records, tableColumns, truncated, rowCap } = this.state;

        // Only filterable columns can be used as search criteria; columns already used by a
        // criterion are hidden so the same column cannot be added twice.
        const usedColumns = new Set(searchCriteria.map(c => c.colName));
        const columnOptions = entityName
            ? (columnsByEntity[entityName] || []).filter(c => c.filterable && !usedColumns.has(c.value))
            : [];

        return (
            <StyledCard block={block} error={error} icon={'fa-solid fa-database'} title={'navbar.EntityRecords'}>
                <Form onSubmit={this.getRecords}>
                    <Row className="align-items-center">
                        <Col className="col-3">
                            <CustomSelect
                                isSearchable
                                options={entityOptions}
                                onChange={this.handleEntity}
                                placeholder={<FormattedMessage id="EntityRecords.Entity" />}
                                required
                            />
                        </Col>
                        <Col className="col-3">
                            <CustomSelect
                                isSearchable
                                options={columnOptions}
                                value={null}
                                onChange={this.handleAddCriterion}
                                placeholder={<FormattedMessage id="EntityRecords.Column" />}
                                isDisabled={!entityName || searchCriteria.length >= this.maxFilters}
                            />
                        </Col>
                        <Col>
                            <CustomInput
                                type="checkbox"
                                id="entityRecordsIncludeDeleted"
                                checked={includeDeleted}
                                onChange={this.toggleIncludeDeleted}
                                label={this.props.intl.formatMessage({ id: "EntityRecords.IncludeDeleted" })}
                            />
                        </Col>
                        <Col className="text-right">
                            <Button className="btn btn-sm btn-host" type="submit" disabled={!entityName || searchCriteria.length === 0}>
                                <i className="fa-solid fa-magnifying-glass" />
                            </Button>
                        </Col>
                    </Row>
                    {
                        searchCriteria.map((crt, idx) =>
                            <Row key={crt.id} className="mt-2 align-items-center">
                                <Col className="col-3" />
                                <Col className="col-3 ml-2">
                                    {crt.colName}
                                </Col>
                                <Col className="col-3">
                                    <Input
                                        type="text"
                                        name="colValue"
                                        onChange={evt => this.handleValueChange(evt, idx)}
                                        value={crt.colValue}
                                        placeholder={this.props.intl.formatMessage({ id: "EntityRecords.Value" })}
                                        required
                                    />
                                </Col>
                                <Col>
                                    <Button className="btn btn-sm btn-host" type="button" onClick={_ => this.removeCriterion(idx)}>
                                        <i className="fa-regular fa-trash-can" />
                                    </Button>
                                </Col>
                            </Row>
                        )
                    }
                </Form>

                {truncated ?
                    <Row className="mt-3">
                        <Col>
                            <div className="text-warning">
                                <i className="fa-solid fa-triangle-exclamation mr-2" />
                                <FormattedMessage id="EntityRecords.Truncated" values={{ cap: rowCap }} />
                            </div>
                        </Col>
                    </Row>
                    : ''}

                {tableColumns && tableColumns.length > 0 ?
                    <Row className="mt-3" style={{ overflowX: 'auto' }}>
                        <Col>
                            <CustomTable
                                data={records}
                                columns={tableColumns}
                                keyField="_rowKey"
                                search={true}
                                exportCSV={true}
                                remote={false}
                                showTotal={true}
                            />
                        </Col>
                    </Row>
                    : ''}
            </StyledCard>
        );
    }
}

export default injectIntl(EntityRecords);
